import * as React from 'react';
import { useQuery } from 'react-query';
import { useUpdateAtom } from 'jotai/utils';

import { useAccessToken } from 'src/hooks/useAccessToken';
import { useFeatureAvailability } from 'src/hooks/useFeatureAvailability';
import { queryFnFetchErrorWrapper } from 'src/api/utils/errorHandlers';
import { setFeatureStatusAtom } from 'src/shared/features/availabilityAtoms';
import { FeatureStatus } from 'src/shared/features/types';
import {
  FEATURE_PROBES,
  FeatureProbeDef,
  classifyAvailability,
} from 'src/shared/features/registry';

// Re-probe roughly every 30s so a feature flips to "enabled" within half a
// minute of the new backend release going live — no page reload needed.
const PROBE_REFETCH_INTERVAL_MS = 30_000;

/**
 * Runs one lightweight probe per rollout feature and publishes the
 * resulting status into `featureAvailabilityAtom`. Rendered once inside the
 * authenticated `AppShell`.
 */
const FeatureProbe: React.FC<{ def: FeatureProbeDef }> = ({ def }) => {
  const accessToken = useAccessToken();
  const setStatus = useUpdateAtom(setFeatureStatusAtom);
  // Read back what has been PUBLISHED rather than this query's own data, and
  // keep both refetch options plain values. `refetchOnWindowFocus` only accepts
  // a predicate from react-query 3.35 onwards, and Netlify installs from
  // yarn.lock (3.34.12) while local node_modules is on 3.39.3 — so the callback
  // form type-checks here and breaks the deploy. The published status is the
  // better source anyway: it is the same value the UI reads, and the atom is
  // sticky-available, so `settled` never flips back.
  const settled = useFeatureAvailability(def.id) === 'available';

  // Resolve with the classification rather than let the query reject.
  //
  // A gated 403 is a SUCCESSFUL probe — `classifyAvailability` maps it to
  // `available`, because the route exists and entitlement is somebody else's
  // problem. react-query has no notion of an expected rejection, though: the
  // QueryClient sets no custom `logger`, so v3's default logger console.errors
  // every rejected query. With five probes re-running on an interval, an
  // account below a gated plan filled the console with identical 403 objects
  // for the whole session — which is how a real Stripe IntegrationError sat
  // unnoticed in prod on 2026-08-31. Classifying inside the queryFn keeps the
  // console for things that are actually wrong.
  //
  // `queryFnFetchErrorWrapper` still wraps the call, so its 401 handling
  // (clear session, reload) is unchanged — only the expected outcomes stop
  // travelling as exceptions.
  const queryFn = React.useMemo(() => {
    const run = queryFnFetchErrorWrapper(() => def.probe(accessToken));
    return async (): Promise<FeatureStatus> => {
      try {
        await run();
        return 'available';
      } catch (error) {
        return classifyAvailability(error);
      }
    };
  }, [accessToken, def]);

  const { data: status } = useQuery<FeatureStatus>(['featureProbe', def.id], queryFn, {
    enabled: !!accessToken,
    retry: false,
    // Stop once the answer is `available`. Not an optimisation to the polling
    // semantics — `setFeatureStatusAtom` is deliberately sticky-available and
    // DISCARDS every result after the first `available`, so these refetches
    // could not change anything the UI shows. They were pure cost: five
    // requests per interval per session, forever, and on a gated endpoint each
    // one a 403 the browser logs at network level whatever we do here. A
    // feature still 404ing keeps polling, which is the case the interval was
    // added for (route appearing mid-deploy).
    refetchInterval: settled ? false : PROBE_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: !settled,
    staleTime: 0,
  });

  React.useEffect(() => {
    // Skip the idle/loading state (no settled result yet) so we never
    // misread an un-run query as "available".
    if (!status || status === 'unknown') return;
    setStatus({ id: def.id, status });
  }, [status, def.id, setStatus]);

  return null;
};

export const FeatureAvailabilityProvider: React.FC = () => (
  // The live API probe is the single source of truth: a 404 (route absent on
  // the old release) flips the feature to the read-only placeholder, and a
  // 200 auto-enables it. Env flags like REACT_APP_SHOW_DELIVERY_ANALYTICS no
  // longer override this — they must not mask a real 404 during the rollout.
  <>
    {FEATURE_PROBES.map((def) => (
      <FeatureProbe key={def.id} def={def} />
    ))}
  </>
);

export default FeatureAvailabilityProvider;
